import { SceneItemInfo, SceneItemClass, InputSourceTypeInfo } from '../../../../types/obs';
import { promisefy } from '../../../../utils/promisefy';
import { ensureOBSPresence } from '../../../../utils/ensureOBSPresence';

type SelectedSourceClassCallback = (
  params: {
    sceneId: string;
    compositionId: string;
    name: string;
  } & Partial<SceneItemInfo>
) => Promise<SceneItemInfo>;

export type EnabledSource = {
  label: string;
  sceneItemClass: SceneItemClass;
  callback: SelectedSourceClassCallback;
};

const defaultSourceCallback: SelectedSourceClassCallback = async ({
  sceneId,
  compositionId,
  name,
  order,
  class: sourceClass,
  ...source
}) => {
  const sceneSources = await promisefy(window.host.getAllSceneItems)({
    id: sceneId,
    videoCompositionId: compositionId,
  });

  const topmostSource = sceneSources?.pop();

  return promisefy(window.host.addSceneItemObsNativeSource)({
    name,
    videoCompositionId: compositionId,
    class: sourceClass,
    sceneId: sceneId,
    visible: true,
    locked: false,
    order: order ?? (topmostSource?.order ?? 0) + 1,
    settings: {},
    ...source,
  } as SceneItemInfo);
};

// const enabledSources: Partial<Record<SceneItemClass, EnabledSource>> = {
//   monitor_capture: {
//     label: 'Display capture',
//     sceneItemClass: 'monitor_capture',
//     async callback(props) {
//       return defaultSourceCallback({ ...props, class: 'monitor_capture' });
//     },
//   },
//   browser_source: {
//     label: 'Browser source',
//     sceneItemClass: 'browser_source',
//     async callback(props) {
//       return defaultSourceCallback({
//         ...props,
//         class: 'browser_source',
//         settings: {
//           url: 'https://www.streamelements.com',
//           width: 1920,
//           height: 1080,
//           reroute_audio: true,
//           ...props.settings,
//         },
//       });
//     },
//   },
//   game_capture: {
//     label: 'Game capture',
//     sceneItemClass: 'game_capture',
//     async callback(params) {
//       return defaultSourceCallback({
//         ...params,
//         class: 'game_capture',
//       } as never);
//     },
//   },
//   image_source: {
//     label: 'Image',
//     sceneItemClass: 'image_source',
//     async callback(props) {
//       return defaultSourceCallback({
//         ...props,
//         class: 'image_source',
//       });
//     },
//   },
//   slideshow_v2: {
//     label: 'Image slide show',
//     sceneItemClass: 'slideshow_v2',
//     async callback(props) {
//       return defaultSourceCallback({ ...props, class: 'slideshow_v2' });
//     },
//   },
//   ffmpeg_source: {
//     label: 'Media source',
//     sceneItemClass: 'ffmpeg_source',
//     async callback(props) {
//       return defaultSourceCallback({ ...props, class: 'ffmpeg_source' });
//     },
//   },
//   text_gdiplus_v3: {
//     label: 'Text',
//     sceneItemClass: 'text_gdiplus_v3',
//     async callback(props) {
//       return defaultSourceCallback({ ...props, class: 'text_gdiplus_v3' });
//     },
//   },
//   dshow_input: {
//     label: 'Video capture',
//     sceneItemClass: 'dshow_input',
//     async callback(props) {
//       return defaultSourceCallback({
//         ...props,
//         preferExistingSourceReference: true,
//         class: 'dshow_input',
//       });
//     },
//   },
//   window_capture: {
//     label: 'Window capture',
//     sceneItemClass: 'window_capture',
//     async callback(props) {
//       return defaultSourceCallback({
//         ...props,
//         preferExistingSourceReference: true,
//         class: 'window_capture',
//       });
//     },
//   },
// };

export async function getEnabledSources(): Promise<Partial<Record<SceneItemClass, EnabledSource>>> {
  await ensureOBSPresence();

  let availableInputSourceTypes: InputSourceTypeInfo[] = [];

  if (!window.host.getAvailableAudioInputSourceTypes) {
    console.info(
      'User has an older version of SE.Live with no API for available audio input source types, using fallback. This will probably just show video source types.'
    );
    availableInputSourceTypes = await promisefy(window.host.getAvailableInputSourceTypes)();
  } else {
    console.log('Using available audio input source types');
    // No Promise.all here is intentional. OBS sometimes can shit itself with parallel calls.
    availableInputSourceTypes.push(...(await promisefy(window.host.getAvailableAudioInputSourceTypes)()));
    availableInputSourceTypes.push(...(await promisefy(window.host.getAvailableVideoInputSourceTypes)()));
  }

  return Object.fromEntries(
    availableInputSourceTypes
      .filter((entry) => !entry.isDeprecated)
      .map((entry) => [
        entry.class,
        {
          label: entry.name,
          sceneItemClass: entry.class,
          callback: ({ name, sceneId, compositionId, ...props }) => {
            return defaultSourceCallback({
              ...props,
              settings: {
                ...entry.defaultSettings,
                ...props.settings,
              },
              class: entry.class,
              name,
              sceneId,
              compositionId,
            });
          },
        },
      ])
  );
}
